import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Store, Phone, Mail, MapPin, ExternalLink, Check, BarChart3, MousePointerClick, Eye, Image as ImageIcon } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import ConfirmDialog from "../components/ConfirmDialog";
import ChipList from "../components/ChipList";
import ImageCropper from "../components/ImageCropper";
import useLocalStorageState from "../hooks/useLocalStorageState";

const STATUS_OPTIONS = ["active", "inactive"];
const COLUMNS = [
  { key: "name", label: "Restaurant", required: true },
  { key: "phone", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "services", label: "Services" },
  { key: "is_active", label: "Status" },
  { key: "payment", label: "Payment" },
  { key: "created_at", label: "Onboarded" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];

const emptyForm = {
  name: "", address: "", phone: "", email: "",
  zomato_url: "", swiggy_url: "", services: [], is_active: true,
};

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";

export default function AdminRestaurants() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [analytics, setAnalytics] = useState(null); // { restaurant, data, loading }

  const [statusSel, setStatusSel] = useState(new Set());
  const toggleStatus = (s) => setStatusSel((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [hiddenCols, setHiddenCols] = useLocalStorageState(
    "admin_restaurants_hidden_cols_v1",
    new Set(["updated_by_label", "updated_at"]),
  );
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/restaurants"),
        api.get("/services"),
        api.get("/admin/payments"),
      ]);
      // Compute paid_until per restaurant from the payment list (max period_end among status=paid).
      const today = new Date().toISOString().slice(0, 10);
      const paidByRid = {};
      for (const p of (r3.data || [])) {
        if (p.status !== "paid") continue;
        const cur = paidByRid[p.restaurant_id];
        if (!cur || p.period_end > cur.paid_until) {
          paidByRid[p.restaurant_id] = { paid_until: p.period_end, period_start: p.period_start };
        }
      }
      const enriched = (r1.data || []).map((r) => {
        const pay = paidByRid[r.id];
        const isPaidNow = pay && pay.period_start <= today && today <= pay.paid_until;
        return { ...r, paid_until: pay?.paid_until || null, is_paid_now: !!isPaidNow };
      });
      setItems(enriched);
      setServices(r2.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing("new"); setForm(emptyForm); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || "", address: r.address || "", phone: r.phone || "", email: r.email || "",
      zomato_url: r.zomato_url || "", swiggy_url: r.swiggy_url || "",
      services: Array.isArray(r.services) ? r.services : [],
      logo_image_id: r.logo_image_id || "",
      is_active: r.is_active !== false,
    });
  };

  const openAnalytics = async (r) => {
    setAnalytics({ restaurant: r, data: null, loading: true });
    try {
      const res = await api.get(`/restaurants/${r.id}/analytics`);
      setAnalytics({ restaurant: r, data: res.data, loading: false });
    } catch (e) {
      toast.error("Failed to load analytics");
      setAnalytics(null);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        email: form.email?.trim() || null,
        logo_image_id: form.logo_image_id || null,
      };
      if (editing === "new") {
        await api.post("/restaurants", payload);
        toast.success("Restaurant created");
      } else {
        await api.patch(`/restaurants/${editing}`, payload);
        toast.success("Restaurant updated");
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const askRemove = (r) => setDeleting(r);
  const confirmRemove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/restaurants/${deleting.id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Failed"); }
    finally { setDeleting(null); }
  };

  const filtered = useMemo(() => {
    let arr = [...items];
    if (statusSel.size > 0) {
      arr = arr.filter((r) => statusSel.has(r.is_active ? "active" : "inactive"));
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter((r) =>
        [r.name, r.phone, r.email, r.address].some((v) => (v || "").toLowerCase().includes(s)),
      );
    }
    const sortValue = (r) => sortKey === "is_active" ? (r.is_active ? "active" : "inactive") : r[sortKey];
    arr.sort((a, b) => compareValues(sortValue(a), sortValue(b), sortDir));
    return arr;
  }, [items, statusSel, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  const onSort = (k) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  return (
    <div data-testid="admin-restaurants" className="p-8 lg:p-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#525252]">Manage</div>
          <h1 className="font-display text-4xl font-bold mt-1">Restaurants</h1>
        </div>
        <button onClick={openCreate} data-testid="add-restaurant-button"
                className="inline-flex items-center gap-2 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-4 py-2 rounded-sm font-medium">
          <Plus size={16}/> Onboard restaurant
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, phone, email, address…"
          data-testid="restaurants-search"
          className="flex-1 min-w-[200px] bg-white border border-black/10 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none"
        />
        <MultiSelectStatusFilter
          options={STATUS_OPTIONS} selected={statusSel} onToggle={toggleStatus}
          testIdPrefix="restaurants-status"
        />
        {statusSel.size > 0 && (
          <button data-testid="restaurants-clear-filter" onClick={() => setStatusSel(new Set())} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="restaurants-cols"/></div>
      </div>

      {loading ? (
        <div className="text-[#525252]" data-testid="restaurants-loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center text-[#525252]" data-testid="restaurants-empty">No restaurants match the current filter.</div>
      ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="restaurants-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("name") && <th className="px-4 py-3 text-left"><SortableHeader label="Restaurant" sortKey="name" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("phone") && <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#525252]">Contact</th>}
                  {showCol("email") && <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#525252]">Email</th>}
                  {showCol("address") && <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#525252]">Address</th>}
                  {showCol("services") && <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#525252]">Services</th>}
                  {showCol("is_active") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="is_active" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("payment") && <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-[#525252]">Payment</th>}
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Onboarded" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.id} data-testid={`restaurant-row-${r.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("name") && (
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {r.logo_image_id ? (
                            <img src={`/api/files/${r.logo_image_id}`} alt="" loading="lazy"
                                 className="w-9 h-9 rounded-sm object-cover bg-[var(--brand-surface)] border border-black/10 shrink-0"/>
                          ) : (
                            <div className="w-9 h-9 rounded-sm bg-[var(--brand-surface)] border border-black/10 shrink-0 inline-flex items-center justify-center text-[#525252]">
                              <Store size={14}/>
                            </div>
                          )}
                          <div className="min-w-0">
                            <Link to={`/admin/restaurants/${r.id}`}
                                  data-testid={`restaurant-open-${r.id}`}
                                  className="font-medium hover:text-[var(--brand-primary)] transition-colors block truncate">
                              {r.name}
                            </Link>
                            <div className="flex gap-3 mt-1">
                              {r.zomato_url && (
                                <a href={r.zomato_url} target="_blank" rel="noopener noreferrer"
                                   data-testid={`restaurant-zomato-${r.id}`}
                                   className="text-[10px] uppercase tracking-wider text-[var(--brand-primary)] inline-flex items-center gap-1 hover:underline">
                                  Zomato <ExternalLink size={10}/>
                                </a>
                              )}
                              {r.swiggy_url && (
                                <a href={r.swiggy_url} target="_blank" rel="noopener noreferrer"
                                   data-testid={`restaurant-swiggy-${r.id}`}
                                   className="text-[10px] uppercase tracking-wider text-[#FC8019] inline-flex items-center gap-1 hover:underline">
                                  Swiggy <ExternalLink size={10}/>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    )}
                    {showCol("phone") && (
                      <td className="px-4 py-3">
                        <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]" data-testid={`restaurant-phone-${r.id}`}>
                          <Phone size={12}/> {r.phone}
                        </a>
                      </td>
                    )}
                    {showCol("email") && (
                      <td className="px-4 py-3 text-[#525252] text-xs">
                        {r.email ? (
                          <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
                            <Mail size={12}/> {r.email}
                          </a>
                        ) : <span className="opacity-50">—</span>}
                      </td>
                    )}
                    {showCol("address") && (
                      <td className="px-4 py-3 text-xs text-[#525252] max-w-[260px]">
                        <span className="inline-flex items-start gap-1"><MapPin size={12} className="mt-0.5 shrink-0"/> <span className="line-clamp-2">{r.address}</span></span>
                      </td>
                    )}
                    {showCol("services") && (
                      <td className="px-4 py-3"><ChipList items={r.services || []} limit={2} testId={`restaurant-services-${r.id}`}/></td>
                    )}
                    {showCol("is_active") && (
                      <td className="px-4 py-3">
                        {r.is_active ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-sm">
                            <Check size={10}/> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-black/5 text-[#525252] border border-black/10 px-2 py-0.5 rounded-sm">
                            Inactive
                          </span>
                        )}
                      </td>
                    )}
                    {showCol("payment") && (
                      <td className="px-4 py-3" data-testid={`restaurant-payment-${r.id}`}>
                        <PaymentStatusPill paidUntil={r.paid_until} isPaidNow={r.is_paid_now}/>
                      </td>
                    )}
                    {showCol("created_at") && <td className="px-4 py-3 text-xs text-[#525252]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-xs text-[#525252]">{r.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-xs text-[#525252]">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openAnalytics(r)} data-testid={`restaurant-analytics-${r.id}`}
                                className="p-2 hover:bg-black/5 rounded-sm text-[var(--brand-accent)]" title="View analytics">
                          <BarChart3 size={16}/>
                        </button>
                        <button onClick={() => openEdit(r)} data-testid={`restaurant-edit-${r.id}`}
                                className="p-2 hover:bg-black/5 rounded-sm" title="Edit">
                          <Pencil size={16}/>
                        </button>
                        <button onClick={() => askRemove(r)} data-testid={`restaurant-delete-${r.id}`}
                                className="p-2 hover:bg-rose-50 text-rose-700 rounded-sm" title="Delete">
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={filtered.length}
                      onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40" data-testid="restaurant-editor-modal">
          <form onSubmit={save} className="bg-white w-full max-w-xl overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-black/10">
              <h2 className="font-display text-2xl font-bold">{editing === "new" ? "Onboard restaurant" : "Edit restaurant"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:bg-black/5" data-testid="restaurant-close">
                <X size={18}/>
              </button>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <Field label="Restaurant name *">
                <input required type="text" value={form.name} data-testid="restaurant-name"
                       onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls}/>
              </Field>
              <ImageCropper
                label="Logo / primary image (optional)"
                fileId={form.logo_image_id}
                onUpload={(id) => setForm({ ...form, logo_image_id: id })}
                onClear={() => setForm({ ...form, logo_image_id: "" })}
                testId="restaurant-logo"
              />
              <Field label="Complete address *">
                <textarea required rows={2} value={form.address} data-testid="restaurant-address"
                          onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls}/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact number *">
                  <input required type="tel" value={form.phone} data-testid="restaurant-phone"
                         placeholder="+91 98765 43210"
                         onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls}/>
                </Field>
                <Field label={<span>Email <span className="text-[#525252] font-normal">(optional)</span></span>}>
                  <input type="email" value={form.email} data-testid="restaurant-email"
                         onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls}/>
                </Field>
              </div>
              <Field label="Zomato profile link">
                <input type="url" value={form.zomato_url} placeholder="https://www.zomato.com/..." data-testid="restaurant-zomato-url"
                       onChange={(e) => setForm({ ...form, zomato_url: e.target.value })} className={inputCls}/>
              </Field>
              <Field label="Swiggy profile link">
                <input type="url" value={form.swiggy_url} placeholder="https://www.swiggy.com/..." data-testid="restaurant-swiggy-url"
                       onChange={(e) => setForm({ ...form, swiggy_url: e.target.value })} className={inputCls}/>
              </Field>

              <ServicePicker value={form.services} options={services} onChange={(arr) => setForm({ ...form, services: arr })}/>

              <Field label="Status">
                <div className="flex gap-2">
                  {[true, false].map((v) => (
                    <button key={String(v)} type="button"
                            data-testid={`restaurant-status-${v ? "active" : "inactive"}`}
                            onClick={() => setForm({ ...form, is_active: v })}
                            className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                              form.is_active === v
                                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                                : "bg-white text-[#525252] border-black/10 hover:border-black/30"
                            }`}>
                      {v ? "Active" : "Inactive"}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="p-6 border-t border-black/10 flex gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setEditing(null)}
                      className="flex-1 px-4 py-2 border border-black/10 rounded-sm hover:bg-black/5">
                Cancel
              </button>
              <button type="submit" disabled={saving} data-testid="restaurant-save"
                      className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-4 py-2 rounded-sm font-medium disabled:opacity-60">
                {saveBtnLabel(saving, editing === "new")}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        onConfirm={confirmRemove}
        title="Delete restaurant?"
        description={`This will permanently remove "${deleting?.name}".`}
        confirmText="Delete"
        destructive
        testId="restaurant-delete-confirm"
      />

      {analytics && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40" data-testid="restaurant-analytics-drawer">
          <div className="bg-white w-full max-w-2xl overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-black/10 sticky top-0 bg-white z-10">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#525252]">Analytics</div>
                <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2"><BarChart3 size={20} className="text-[var(--brand-accent)]"/> {analytics.restaurant.name}</h2>
              </div>
              <button type="button" onClick={() => setAnalytics(null)} className="p-1 hover:bg-black/5" data-testid="analytics-close">
                <X size={18}/>
              </button>
            </div>
            <div className="p-6 flex-1">
              {analytics.loading ? (
                <div className="text-[#525252]">Loading…</div>
              ) : (
                <RestaurantAnalyticsBody data={analytics.data}/>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="block text-sm font-medium mb-2">{label}</label>{children}</div>);
}

function saveBtnLabel(saving, isNew) {
  if (saving) return "Saving…";
  return isNew ? "Onboard" : "Save changes";
}

function RestaurantAnalyticsBody({ data }) {
  if (!data) return null;
  const t = data.totals || {};
  const cards = [
    { label: "Total campaigns", value: t.campaigns, sub: `${t.active_campaigns} active`, icon: BarChart3 },
    { label: "Portfolios", value: t.portfolios, sub: `${t.approved_portfolios} approved`, icon: ImageIcon },
    { label: "Ad views", value: t.ad_views_total, sub: "lifetime", icon: Eye },
    { label: "Ad clicks", value: t.ad_clicks_total, sub: `CTR ${t.ctr_percent}%`, icon: MousePointerClick },
  ];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-3" data-testid="analytics-totals">
        {cards.map((c) => {
          const Ic = c.icon;
          return (
            <div key={c.label} className="border border-black/10 p-4 rounded-sm bg-[var(--brand-surface)]">
              <div className="flex items-center justify-between text-[#525252]">
                <span className="text-[10px] uppercase tracking-wider">{c.label}</span>
                <Ic size={14}/>
              </div>
              <div className="mt-2 font-display text-3xl font-bold">{c.value ?? 0}</div>
              <div className="text-[11px] text-[#525252] mt-0.5">{c.sub}</div>
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><ImageIcon size={16}/> Portfolios <span className="text-[#525252] text-xs">({data.portfolios.length})</span></h3>
        {data.portfolios.length === 0 ? (
          <p className="text-sm text-[#525252]">No portfolios linked yet.</p>
        ) : (
          <ul className="border border-black/10 rounded-sm divide-y divide-black/5" data-testid="analytics-portfolios">
            {data.portfolios.map((p) => (
              <li key={p.id} className="px-4 py-2 flex items-center justify-between text-sm">
                <span className="font-medium">{p.title}</span>
                <span className="text-xs text-[#525252]">{p.category} · {p.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="font-display text-lg font-bold mb-3 inline-flex items-center gap-2"><BarChart3 size={16}/> Advertisements <span className="text-[#525252] text-xs">({data.ads.length})</span></h3>
        {data.ads.length === 0 ? (
          <p className="text-sm text-[#525252]">No advertisements linked yet.</p>
        ) : (
          <ul className="border border-black/10 rounded-sm divide-y divide-black/5" data-testid="analytics-ads">
            {data.ads.map((a) => (
              <li key={a.id} className="px-4 py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{a.restaurant_name}</div>
                  <div className="text-[11px] text-[#525252] mt-0.5">{a.status} · {a.is_active ? "active" : "inactive"}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="inline-flex items-center gap-1 text-[#525252]"><Eye size={12}/> {a.view_count || 0}</div>
                  <div className="inline-flex items-center gap-1 text-[#525252] ml-3"><MousePointerClick size={12}/> {a.click_count || 0}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ServicePicker({ value = [], options = [], onChange }) {
  const toggle = (t) => {
    if (value.includes(t)) onChange(value.filter((v) => v !== t));
    else onChange([...value, t]);
  };
  return (
    <div>
      <label className="block text-sm font-medium mb-2">Services availed</label>
      {options.length === 0 ? (
        <p className="text-xs text-[#525252]">No services available. Create services first.</p>
      ) : (
        <div className="border border-black/10 rounded-sm p-3 bg-[#FAFAFA] max-h-48 overflow-y-auto" data-testid="restaurant-services-picker">
          <div className="grid grid-cols-1 gap-1.5">
            {options.map((o) => {
              const checked = value.includes(o.title);
              return (
                <label key={o.id || o.title} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-2 py-1.5 rounded-sm">
                  <input type="checkbox" data-testid={`restaurant-service-${o.slug || o.title}`} checked={checked} onChange={() => toggle(o.title)} className="w-4 h-4"/>
                  <span className="flex-1">{o.title}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentStatusPill({ paidUntil, isPaidNow }) {
  if (!paidUntil) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border border-rose-200 bg-rose-50 text-rose-700">
        Unpaid
      </span>
    );
  }
  const end = new Date(paidUntil);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((end - today) / 86400000);
  let tone, label;
  if (!isPaidNow && daysLeft < 0) {
    tone = "border-rose-200 bg-rose-50 text-rose-700";
    label = `Expired ${Math.abs(daysLeft)}d ago`;
  } else if (daysLeft <= 7) {
    tone = "border-amber-200 bg-amber-50 text-amber-800";
    label = daysLeft === 0 ? "Ends today" : `${daysLeft}d left`;
  } else {
    tone = "border-emerald-200 bg-emerald-50 text-emerald-700";
    label = `Paid · ${daysLeft}d left`;
  }
  const dateStr = end.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  return (
    <span title={`Paid until ${dateStr}`}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${tone}`}>
      {label}
    </span>
  );
}

